import { useState, useEffect, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { startOfDay, format, addDays } from 'date-fns';
import {
  GET_MY_SCHEDULE_JOBS
} from '../graphql/queries';
import { formatStartOfDay, formatEndOfDay } from '../utils/timezone-utils';
import { logger } from '../utils/logger';

// Maximum jobs per page - reduced for better performance
const JOBS_PER_PAGE = 25; // Reduced from 50 to 25 for faster initial load

// 🔒 SECURITY NOTE: Using GET_MY_SCHEDULE_JOBS from queries.js
// which uses the secure myScheduleJobs endpoint that only returns directly assigned jobs

// Helper function to consistently format dates to YYYY-MM-DD
const formatDateKey = (date) => {
  try {
    let dateObj;

    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      if (/^\d+$/.test(date)) {
        dateObj = new Date(parseInt(date, 10));
      } else if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date; // Already in correct format
      } else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      return null;
    }

    if (isNaN(dateObj.getTime())) {
      return null;
    }

    // Format as YYYY-MM-DD
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    return null;
  }
};

// Group jobs by date
const groupJobsByDate = (jobs) => {
  const grouped = {};

  if (!jobs || !Array.isArray(jobs)) return grouped;

  jobs.forEach(job => {
    const dateKey = formatDateKey(job.scheduledDate);
    if (!dateKey) return;

    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }

    grouped[dateKey].push(job);
  });

  // Sort each date's jobs
  Object.keys(grouped).forEach(dateKey => {
    grouped[dateKey].sort((a, b) => {
      // Sort by time if available, otherwise default sort
      if (a.startTime && b.startTime) {
        return new Date(a.startTime) - new Date(b.startTime);
      }
      return 0;
    });
  });

  return grouped;
};

export function useMyScheduleJobs(user, searchTerm = '', page = 1) {
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [forcedRefresh, setForcedRefresh] = useState(false);

  // Add state for accumulated jobs
  const [accumulatedJobs, setAccumulatedJobs] = useState([]);
  const [loadedPages, setLoadedPages] = useState(new Set([1])); // Track which pages we've loaded

  // 🔒 SECURE: Fetch pending jobs assigned to the user using secure myScheduleJobs endpoint
  // This includes ALL job categories (CLEANING, INSPECTION, MAINTENANCE, WALKTHROUGH, CUSTOM)
  // 🎯 FIX: Removed date filtering - users should see ALL their assigned jobs regardless of date
  const {
    loading: pendingLoading,
    data: pendingData,
    refetch: refetchPending,
    fetchMore: fetchMorePending,
    error: pendingError
  } = useQuery(GET_MY_SCHEDULE_JOBS, {
    variables: {
      input: {
        // Removed date filtering - users should see ALL their assigned jobs regardless of date
        assignedToUserId: user?.id ? parseInt(user.id, 10) : undefined,
        statuses: ['PENDING', 'IN_PROGRESS'],
        search: searchTerm && searchTerm.length >= 2 ? searchTerm : undefined,
        limit: JOBS_PER_PAGE,
        offset: (page - 1) * JOBS_PER_PAGE
      }
    },
    skip: !user?.id,
    fetchPolicy: 'network-only', // 🔒 SECURITY: Force fresh data to prevent cross-user cache leakage
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    onCompleted: (data) => {
      logger.debug('MySchedule query completed', {
        jobCount: data?.myScheduleJobs?.items?.length || 0,
        hasMore: data?.myScheduleJobs?.hasMore,
        jobs: data?.myScheduleJobs?.items?.map(j => ({ id: j.id, status: j.status, property: j.property?.name })) || []
      });

      if (page === 1 && data?.myScheduleJobs?.items) {
        // 🔒 SECURITY: Include ALL jobs assigned to the user, regardless of category
        // The backend ensures only directly assigned jobs are returned
        // Additional security check: verify all jobs are assigned to current user OR their teams
        const allAssignedJobs = data.myScheduleJobs.items.filter(job => {
          const isValidStatus = job.status === 'PENDING' || job.status === 'IN_PROGRESS';
          const isDirectlyAssigned = job.assignedTo?.id === user?.id;
          const isTeamAssigned = job.assignedTeamId && !job.assignedTo; // Team assignment without specific user
          const isAssignedToUser = isDirectlyAssigned || isTeamAssigned;

          // 🔒 SECURITY: Jobs are pre-filtered by backend to only include user's teams
          // So if we receive it, the user has permission to see it
          if (!isAssignedToUser && job.assignedTo?.id !== user?.id) {
            logger.debug('Team assignment job received', {
              jobId: job.id,
              property: job.property?.name,
              assignedToId: job.assignedTo?.id,
              assignedTeamId: job.assignedTeamId,
              currentUserId: user?.id
            });
          }

          // 🐛 DEBUG: Log jobs that have invalid statuses to help debug the refresh issue
          if (!isValidStatus) {
            logger.debug('Filtering out job with invalid status', {
              jobId: job.id,
              status: job.status,
              property: job.property?.name,
              expectedStatuses: ['PENDING', 'IN_PROGRESS']
            });
          }

          return isValidStatus;
        });

        setAccumulatedJobs(allAssignedJobs);
        setLoadedPages(new Set([1]));
      }

      setHasMore(data?.myScheduleJobs?.hasMore || false);
      setIsLoadingMore(false);
      setForcedRefresh(false);
    },
    onError: (error) => {
      setIsLoadingMore(false);
      setForcedRefresh(false);
    }
  });

  // 🔒 SECURE: Process and group ALL pending jobs by date
  // 🎯 FIX: Jobs from previous days (up to 7 days back) are now included to prevent incomplete jobs from disappearing
  // This now includes ALL job categories assigned to the user
  const myPendingJobs = useMemo(() => {
    // Use accumulated jobs instead of just the current page
    // Fallback to the current page data if no accumulated jobs yet
    const jobs = accumulatedJobs.length > 0 ? accumulatedJobs : (pendingData?.myScheduleJobs?.items || []);

    // 🔒 SECURITY: Include ALL jobs assigned to the user OR their teams
    // The backend ensures only jobs the user has access to are returned
    // Trust backend filtering and only filter by status
    const allAssignedJobs = jobs.filter(job => {
      const isValidStatus = job.status === 'PENDING' || job.status === 'IN_PROGRESS';

      // 🐛 DEBUG: Log jobs that have invalid statuses at display time
      if (!isValidStatus) {
        logger.debug('MySchedule filtering out job at display time', {
          jobId: job.id,
          status: job.status,
          property: job.property?.name,
          expectedStatuses: ['PENDING', 'IN_PROGRESS']
        });
      }

      // Backend already ensures security - only filter by status
      return isValidStatus;
    });

    return groupJobsByDate(allAssignedJobs);
  }, [pendingData, pendingError, accumulatedJobs, user?.id]);

  // Update hasMore based on API response
  useEffect(() => {
    if (pendingData?.myScheduleJobs) {
      setHasMore(pendingData.myScheduleJobs.hasMore);
    }
  }, [pendingData]);

  // Function to load more jobs - now manual instead of automatic
  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    try {
      const nextPage = Math.max(...Array.from(loadedPages)) + 1;
      const result = await fetchMorePending({
        variables: {
          input: {
            startDate: formatStartOfDay(new Date()), // Start from today
            endDate: formatEndOfDay(addDays(new Date(), 30)), // Look ahead 30 days
            assignedToUserId: user?.id ? parseInt(user.id, 10) : undefined,
            statuses: ['PENDING', 'IN_PROGRESS'],
            search: searchTerm && searchTerm.length >= 2 ? searchTerm : undefined,
            limit: JOBS_PER_PAGE,
            offset: (nextPage - 1) * JOBS_PER_PAGE
          }
        }
      });

      if (result.data?.myScheduleJobs?.items) {
        // 🔒 SECURITY: Include ALL jobs assigned to the user, regardless of category
        // Additional security check: verify all jobs are assigned to current user
        const allAssignedJobs = result.data.myScheduleJobs.items.filter(job => {
          const isValidStatus = (job.status === 'PENDING' || job.status === 'IN_PROGRESS');
          const isAssignedToCurrentUser = job.assignedTo?.id === user?.id;

          // 🔒 SECURITY: Double-check that job is assigned to current user
          if (!isAssignedToCurrentUser) {
            logger.warn('Job not assigned to current user in load more', {
              jobId: job.id,
              assignedToId: job.assignedTo?.id,
              currentUserId: user?.id
            });
            return false;
          }

          return isValidStatus && isAssignedToCurrentUser;
        });

        setAccumulatedJobs(prev => [...prev, ...allAssignedJobs]);
        setLoadedPages(prev => {
          const newSet = new Set(prev);
          newSet.add(nextPage);
          return newSet;
        });
        setHasMore(result.data.myScheduleJobs.hasMore);
      }
    } catch (error) {
      logger.error('Error loading more jobs', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Function to refetch all data
  const refetchAllJobs = (forceNetworkRefresh = false) => {
    logger.debug('MySchedule refetching all jobs', { forceNetworkRefresh });

    // Reset accumulated jobs and pages
    setAccumulatedJobs([]);
    setLoadedPages(new Set([1]));

    // Refetch pending jobs query with appropriate fetch policy
    const fetchPolicy = forceNetworkRefresh ? 'network-only' : 'cache-and-network';
    refetchPending({ fetchPolicy });
  };

  // Reset accumulated jobs when search term or user changes
  useEffect(() => {
    setAccumulatedJobs([]);
    setLoadedPages(new Set([1]));
  }, [searchTerm, user?.id]); // Removed selectedCompletedJobsDate since we don't use completed jobs

  // 🔒 SECURITY: Additional effect to clear jobs when user changes
  useEffect(() => {
    // Clear any accumulated jobs when user changes to prevent cross-user data leakage
    setAccumulatedJobs([]);
    setLoadedPages(new Set([1]));
  }, [user?.id]);

  // 🔒 SECURITY: Force refresh function to clear stale cache
  const forceRefresh = async () => {
    setForcedRefresh(true);
    setAccumulatedJobs([]);
    setLoadedPages(new Set([1]));

    try {
      await refetchPending();
    } catch (error) {
      logger.error('Error during force refresh', error);
    } finally {
      setForcedRefresh(false);
    }
  };

  return {
    myPendingJobs,
    jobsLoading: pendingLoading, // Only pending jobs loading for the main "Jobs To Complete" section
    isLoadingMore,
    hasMore,
    handleLoadMore,
    refetchAllJobs,
    forceRefresh, // 🔒 SECURITY: Expose force refresh for cache clearing
    totalLoadedJobs: accumulatedJobs.length // Return count of loaded jobs for UI feedback
  };
}
